import { SiteUrls } from '@tableau/shared-urls';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import * as _ from 'underscore';

import { CollectionItemsResourceQuery } from '~src/app/spaces/Collections/ResourceQuery/CollectionItemsResourceQuery';

import { Formatters, Messages } from '../../../../../codegen/Localize';
import { showActionableToast } from '../../../../../common/react/components/ActionableMessage';
import { Location } from '../../../../../common/react/Location';
import { IDataSourceInfo } from '../../../../../datasource/models/IDataSourceInfo';
import { deepClone } from '../../../../../ts/helpers/DeepClone';
import { CollectionsErrorCode } from '../../../../../ts/helpers/ErrorCodes';
import { ServerFilterClauses } from '../../../../../ts/helpers/ServerFilterClauses';
import { L10n } from '../../../../../ts/L10n/L10n';
import { ExplorableItemType } from '../../../../../ts/models/ContentType';
import { IContentInfo } from '../../../../../ts/models/IContentInfo';
import { IFilterClause } from '../../../../../ts/ServerApi/types/IFilterClause';
import { IToastOptions, ToasterService, ToastType } from '../../../../../ts/services/ToasterService';
import {
  ChangeCollectionVisibility,
  CreateEmptyCollection,
  CreateNoNameCollection,
  OpenManageItemsInCollectionsDialog,
  RemoveFromThisCollection} from '../../../../actions/AppActionTypes';
import { requestFailed } from '../../../../actions/RequestFailedAction';
import { ChangeCollectionVisibilityAction } from '../../../../actions/spaces/Collections/ChangeCollectionVisibilityAction';
import { ManageItemsInCollectionsDialogAction, openManageItemsInCollectionsDialogAction } from '../../../../actions/spaces/Collections/ManageItemsInCollectionAction';
import { RemoveFromThisCollectionAction } from '../../../../actions/spaces/Collections/RemoveFromThisCollectionAction';
import { CreateEmptyCollectionAction, createEmptyCollectionAction,CreateNoNameCollectionAction } from '../../../../actions/spaces/ContentExplorerCreateActions';
import { requestSpaceContents } from '../../../../actions/spaces/RequestSpaceContentsActions';
import { CompositeSessionInfoSelectors, SessionUserSelectors } from '../../../../selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '../../../../selectors/SiteUrlsSelectors';
import { selectSpaceInfo,selectSpaceInfoItem } from '../../../../selectors/SpaceInfoSelectors';
import { ChangeCollectionVisibilityDialog } from '../../../../spaces/Collections/ChangeCollectionVisibilityDialog';
import { COLLECTION_NAME_MAX_LENGTH } from '../../../../spaces/Collections/CollectionHeaderEditableName';
import { AddRemoveCollectionItemsData } from '../../../../spaces/Collections/CollectionModels';
import { sendManageItemsEvent } from '../../../../spaces/Collections/CollectionsTelemetry';
import { CreateCollectionDialog } from '../../../../spaces/Collections/CreateCollectionDialog';
import { ICollectionInfo } from '../../../../spaces/Collections/ICollectionInfo';
import { ManageItemsInCollectionsDialog } from '../../../../spaces/Collections/ManageItemsInCollectionsDialog';
import { CollectionsResourceQuery } from '../../../../spaces/Collections/ResourceQuery/CollectionsResourceQuery';
import { SpaceInfo } from '../../../../state/SpaceInfo';
import { reloadSpace, reloadSpaceIfUpdatingCurrentSpace } from '../RefreshContentSagas';

export function getItemsWithCorrectLuids(items: IContentInfo[]): IContentInfo[] {
  // Data Sources from CRASS/content exploration have two luids: datasourceLuid and luid. All REST APIs operate on datasourceLuid
  // so override the luid property with the datasourceLuid for data sources.
  // getDatasource(s) and exploreContent return only one luid for data sources (the one that REST APIs use) so don't need to
  // transform data sources from those APIs.
  return items.map((item: IContentInfo<ExplorableItemType>) => {
    if (item.hasOwnProperty('datasourceLuid') && item.contentType === 'datasource') {
      return replaceLuidWithDataSourceLuid(item);
    } else {
      return item;
    }
  });
}

function replaceLuidWithDataSourceLuid(item: IContentInfo<ExplorableItemType>) {
  // override the luid with the datasource luid for using a consistent luid in collections
  let ds = deepClone(item);
  ds.luid = (item as IDataSourceInfo).datasourceLuid;
  return ds;
}

// Create Empty Collection- this is from the ManageItemsInCollections/CreateCollectionDialog
export function* handleCreateEmptyCollection(action: CreateEmptyCollectionAction) {
  try {
    const urls: SiteUrls = yield select(siteUrlsSelector);
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const currentUserId: string =  yield select(SessionUserSelectors.sessionUserId);
    const isAdmin = yield select(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);
    const collectionsCountForUser = yield call(getEditableCollectionsCount, isAdmin, currentUserId);

    const addItemsToCollection: ICollectionInfo = yield call(CreateCollectionDialog.open, urls, spaceInfo, collectionsCountForUser < 1, action.itemsToPossiblyAdd);
    if (addItemsToCollection) {
      if (_.isEmpty(addItemsToCollection)) {
        // no collection added
        yield put(openManageItemsInCollectionsDialogAction(action.itemsToPossiblyAdd, [], action.manageItemsTelemetry));
      } else {
        yield put(openManageItemsInCollectionsDialogAction(action.itemsToPossiblyAdd, [addItemsToCollection.luid], action.manageItemsTelemetry));
      }
    } else {
      if (action.manageItemsTelemetry) {
        action.manageItemsTelemetry.result = 'cancel-from-create';
        const spaceInfo = yield select(selectSpaceInfo);
        sendManageItemsEvent(action.manageItemsTelemetry, spaceInfo);
      }
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));

    if (action.manageItemsTelemetry) {
      action.manageItemsTelemetry.result = 'cancel-from-create';
      const spaceInfo = yield select(selectSpaceInfo);
      sendManageItemsEvent(action.manageItemsTelemetry, spaceInfo);
    }
  }
}

// Create Empty Collection without a name-- this is for creating a collection from the
// Create button
export function* handleCreateNoNameCollection(action: CreateNoNameCollectionAction) {
  try {
    const urls = yield select(siteUrlsSelector);
    const resource = new CollectionsResourceQuery();
    const currentUrl = Location.instance.href();
    const userDisplayName = yield select(SessionUserSelectors.sessionUserDisplayName);
    yield call(createCollectionAndShowToasts, resource, urls, currentUrl, userDisplayName);

  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function getCollectionName(displayName: string): string {
  let collectionName = Messages.collectionName({ USER_DISPLAY_NAME: displayName });

  // If too long, truncate display name. Leave room for a space plus 3 digit number appended, e.g. "User's Collection 999".
  const maxLength = COLLECTION_NAME_MAX_LENGTH - 4;
  if(collectionName.length > maxLength) {
    const numCharsToTruncate = collectionName.length - maxLength;
    const truncatedDisplayName = displayName.slice(0, -numCharsToTruncate).trim();
    collectionName = Messages.collectionName({ USER_DISPLAY_NAME: truncatedDisplayName });
  }

  return collectionName;
}

export async function createCollectionAndShowToasts(
  resource: CollectionsResourceQuery, urls: SiteUrls, currentUrl: string, userDisplayName: string): Promise<void> {
  return resource.create(getCollectionName(userDisplayName))
    .then((collectionInfo: ICollectionInfo) => {
        // navigate to the new collection
        navigateToCollectionAndShowToasts(urls, collectionInfo, resource, currentUrl, undoFailed);
    })
    .catch((result: any) => {
      const errorCode = result && result.data && result.data.errorCode;
      if (errorCode === CollectionsErrorCode.DUPLICATE_KEY) {
        // If for some reason this failed and there was still a duplicate name, just try again
       createCollectionAndShowToasts(resource, urls, currentUrl, userDisplayName);
      } else {
        const toaster = ToasterService.instance;
        const toastOps: IToastOptions = {
          type: ToastType.Error,
          text: Messages.errorUnknown(),
          testId: 'create-no-name-collection-error'
        };
        toaster.pop(toastOps);
      }
    });

    function undoFailed() {
        const toaster: ToasterService = ToasterService.instance;
        toaster.markToastsAsDoNotDismissOnLocationChange();
        toaster.pop({
            type: ToastType.Error,
            text: Messages.deleteCollectionsError({
                ERROR_COUNT: 1,
                ERROR_COUNT_LOC: Formatters.formatCount(1),
            })
        });
    }
}


function navigateToCollectionAndShowToasts(urls: SiteUrls, collectionInfo: ICollectionInfo, resource: CollectionsResourceQuery, currentUrl: string, undoFailed: () => void) {
  Location.instance.setUrl(urls.collectionUrl(collectionInfo.luid));

  // show toast with undo *after* navigating to the collection
  _.defer(() => showActionableToast({
    testId: 'collection-creation-success',
    message: Messages.createCollectionSuccess({ NEW_ITEM_NAME: L10n.elideIfLong(collectionInfo.name) }),
    actionMessage: Messages.createCollectionUndo(),
    action: () => {
      resource.delete([collectionInfo.luid])
        .then(() => {
          Location.instance.setUrl(currentUrl);
        })
        .catch(() => {
          undoFailed();
        });
    }
  },
    ToastType.Success)
  );
}

// AddItemsDialog
export function* handleManageItemsInCollectionDialog(action: ManageItemsInCollectionsDialogAction) {
  try {
    const targetItemsWithCorrectLuids = getItemsWithCorrectLuids(action.targetItems);
    const currentUserId: string =  yield select(SessionUserSelectors.sessionUserId);
    const isAdmin = yield select(CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector);
    const collectionsCountForUser = yield call(getEditableCollectionsCount, isAdmin, currentUserId);

    action.manageItemsTelemetry.numItemsToBeAdded = action.targetItems.length;

    // TFS 1226348: fetching collections from CRASS immediately after creating a collection may omit the newly created collection,
    // check there are no newly created collections (targetCollectionLuids is undefined) before going to create dialog
    if (collectionsCountForUser < 1 && !action.targetCollectionLuids) {
      action.manageItemsTelemetry.firstCollectionFlow = true;
      yield put(createEmptyCollectionAction(targetItemsWithCorrectLuids, action.manageItemsTelemetry));
    } else {
      if (action.targetCollectionLuids?.length > 0) {
        action.manageItemsTelemetry.numCollectionsCreated = action.manageItemsTelemetry.numCollectionsCreated + 1;
      }
      const siteUrls: SiteUrls = yield select(siteUrlsSelector);
      const spaceInfo = yield select(selectSpaceInfo);

      const dialogOptions: ManageItemsInCollectionsDialog.Options = {
        items: targetItemsWithCorrectLuids,
        targetCollectionLuids: action.targetCollectionLuids,
        userId: currentUserId,
        isAdmin,
        siteUrls,
        spaceInfo,
        manageItemsTelemetry: action.manageItemsTelemetry
      };

      yield call(ManageItemsInCollectionsDialog.open, dialogOptions);
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));

    action.manageItemsTelemetry.result = 'cancel';
    const spaceInfo = yield select(selectSpaceInfo);
    sendManageItemsEvent(action.manageItemsTelemetry, spaceInfo);
  }
}

export function getEditableCollectionsCount(isAdmin: boolean, currentUserId: string) {
  // This might be better as a filter isEditable in CS

  let filters: IFilterClause[] = [];
  if (!isAdmin) {
    // get only collections owned by user if user is not an admin
    const ownerFilter: IFilterClause = ServerFilterClauses.serverFilterClauses.owner(currentUserId);
    filters = [ownerFilter];
  }

  return new CollectionsResourceQuery()
    .withFilters(filters)
    .getCount();
}

// Change Collection Visibility
export function* handleChangeCollectionVisibility(action: ChangeCollectionVisibilityAction) {
  // TFS 1237812: Remove with CollectionsPermissions (collectionsPermissionsEnabled) feature flag
  try {
    try {
      yield call(ChangeCollectionVisibilityDialog.open, action.targetItem);
    } catch {
      return;
    }

    const maybeReloaded = yield call(reloadSpaceIfUpdatingCurrentSpace, [action.targetItem]);
    if (!maybeReloaded) {
      yield put(requestSpaceContents());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* handleRemoveFromThisCollection(action: RemoveFromThisCollectionAction) {
  try {
    const collection: ICollectionInfo = yield select(selectSpaceInfoItem);

    // do this everywhere for collection items
    const targetItemsWithCorrectLuids = getItemsWithCorrectLuids(action.targetItems);
    const response: AddRemoveCollectionItemsData = yield call(removeFromThisCollection, collection, targetItemsWithCorrectLuids);

    // there is a scenario where the number removed isn't equal to the requested
    // number. saving the higher value to use
    const numRemoved = Math.max(
      targetItemsWithCorrectLuids.length-response.errors.length,
        response.numItemsSuccessfullyUpdated);

    // there might be a case to do this regardless to avoid inconsistencies with data on the server
    if(numRemoved > 0) {
      yield call(reloadSpace);
    }

    // in the case of one successful removal request, we have all the info to display a more detailed toast
    if(targetItemsWithCorrectLuids.length === 1 && numRemoved === 1) {
      const message = Messages.removedSingleItemFromCollection({
        ITEM_NAME: targetItemsWithCorrectLuids[0].name,
        COLLECTION_NAME: collection.name
      });

      yield call(showRemoveItemToast, message, ToastType.Success);
    } else if(numRemoved > 0) {
      const message = Messages.removedMultipleItemsFromCollection({
        ITEM_COUNT: numRemoved,
        ITEM_COUNT_LOC: Formatters.formatCount(numRemoved),
        COLLECTION_NAME: collection.name
      });

      yield call(showRemoveItemToast, message, ToastType.Success);
    }

    if(response.errors.length > 0) {
      let message;
      if(response.errors.length === 1) {
        message = Messages.removeSingleItemFromCollectionError({
          CONTENT_NAME: L10n.elideIfLong(response.errors[0].erroredItem.contentName),
          COLLECTION_NAME: L10n.elideIfLong(collection.name)
        });
      } else {
        message = Messages.removeItemsFromCollectionErrors({
          ITEM_COUNT: response.errors.length,
          ITEM_COUNT_LOC: Formatters.formatCount(response.errors.length),
          COLLECTION_NAME: L10n.elideIfLong(collection.name)
        });
      }

      yield call(showRemoveItemToast, message, ToastType.Error);
    }

  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function showRemoveItemToast(message: string, toastType: ToastType) {
  const toaster = ToasterService.instance;
  const toastOps: IToastOptions = {
    type: toastType,
    text: message,
    testId: 'removeItemToast'
  };
  toaster.pop(toastOps);
}

export function removeFromThisCollection(collection: ICollectionInfo, items: IContentInfo[]) {
  const resource = new CollectionItemsResourceQuery(collection.luid);
  return resource.removeItemsFromCollection(items);
}

export function* rootCollectionSaga(): SagaIterator {
  yield takeLatest(ChangeCollectionVisibility, handleChangeCollectionVisibility);  // Remove with CollectionsPermissions (collectionsPermissionsEnabled) feature flag
  yield takeLatest(CreateEmptyCollection, handleCreateEmptyCollection);
  yield takeLatest(OpenManageItemsInCollectionsDialog, handleManageItemsInCollectionDialog);
  yield takeLatest(CreateNoNameCollection, handleCreateNoNameCollection);
  yield takeLatest(RemoveFromThisCollection, handleRemoveFromThisCollection);
}
